<template>
  <button
    type="button"
    :class="[
      paddingClass,
      fontSizeClass,
      leadingClass,
      colorClass,
      shadowClass,
      radiusClass,
      'font-sans inline-flex items-center justify-center border border-transparent rounded-md transition ease-in-out duration-100 relative',
    ]"
    :style="customCircleSize"
    @click="handleClick"
  >
    <div v-show="loading" class="absolute flex items-center">
      <base-spinner :size="spinnerSize" current />
    </div>
    <div :class="[{ invisible: loading }, 'inline-flex items-center']">
      <div :class="['h-3.5 w-3.5', iconClass, iconColorClass, fontSizeClass]" v-if="$slots.icon">
        <slot name="icon" />
      </div>
      <slot />
    </div>
  </button>
</template>

<script>
import BaseSpinner from "@/components/base/BaseSpinner";

export default {
  components: {
    BaseSpinner,
  },
  emits: ["click"],
  props: {
    type: {
      type: String,
      default: "primary",
      validator: (value) => ["primary", "secondary", "flat", "text"].indexOf(value) !== -1,
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["xs", "sm", "md", "lg", "xl"].indexOf(value) !== -1,
    },
    color: {
      type: String,
      default: "primary",
      validator: (value) => ["primary", "secondary", "red", "green", "gray", "yellow"].indexOf(value) !== -1,
    },
    disabled: Boolean,
    loading: Boolean,
    circle: { type: Boolean, default: false },
    circleRadius: Number,
  },
  computed: {
    paddingClass() {
      if (this.type === "text") return "";

      switch (this.size) {
        case "xs":
          return "px-2.5 py-1.5";
        case "sm":
          return "px-3 py-2";
        case "md":
        case "lg":
          return "px-4 py-2";
        case "xl":
          return "px-6 py-3";
      }
      return "";
    },
    radiusClass() {
      if (this.circle === true) return "rounded-full flex items-center justify-center font-mono text-center";
      else return "";
    },
    customCircleSize() {
      if (this.circleRadius > 0) return "height: " + this.circleRadius + "px !important; width: " + this.circleRadius + "px !important;";
      else return "";
    },
    iconClass() {
      return this.circle === true ? this.radiusClass : "-ml-1 mr-1.5 leading-3";
    },
    fontSizeClass() {
      switch (this.size) {
        case "xs":
          return "text-xs";
        case "sm":
        case "md":
          return "text-sm";
        case "lg":
        case "xl":
          return "text-base";
      }
      return "";
    },
    leadingClass() {
      switch (this.size) {
        case "sm":
          return "leading-4";
      }
      return "";
    },
    colorClass() {
      if (this.disabled) {
        switch (this.type) {
          case "primary":
            return "text-gray-600 bg-gray-200 cursor-not-allowed";
          case "secondary":
            return "text-gray-500 bg-gray-100 cursor-not-allowed";
          case "flat":
          case "text":
            return "text-gray-500 cursor-not-allowed";
        }
      } else {
        switch (this.type) {
          case "primary":
            return `text-white bg-${this.color}-500 hover:bg-${this.color}-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${this.color}-500`;
          case "secondary":
            return `text-${this.color}-600 bg-${this.color}-100 hover:bg-${this.color}-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${this.color}-500`;
          case "flat":
            return `text-${this.color}-500 bg-transparent hover:bg-${this.color}-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${this.color}-500`;
          case "text":
            return `text-${this.color}-500 bg-transparent hover:underline border-0 focus:outline-none focus:text-${this.color}-700`;
        }
      }
      return "";
    },
    shadowClass() {
      return this.type === "primary" ? "shadow" : "shadow-none";
    },
    spinnerSize() {
      switch (this.size) {
        case "xs":
        case "sm":
          return 16;
        case "md":
          return 24;
        case "lg":
        case "xl":
          return 28;
      }

      return 24;
    },
    iconColorClass() {
      if (this.disabled) {
        switch (this.type) {
          case "primary":
            return "text-gray-600";
          case "secondary":
            return "text-gray-500";
          case "flat":
            return "text-gray-500";
        }
      } else {
        switch (this.type) {
          case "primary":
            return `text-${this.color}-100`;
          case "secondary":
            return `text-${this.color}-300`;
          case "flat":
            return `text-${this.color}-500`;
        }
      }

      return "";
    },
  },
  methods: {
    handleClick() {
      if (!this.disabled && !this.loading) {
        this.$emit("click");
      }
    },
  },
};
</script>
